/**
 * SPEC-AgentUI — agent list view route wrapper.
 *
 * The route `/qa/agent` is reserved for users with `role === 'agent'`. A
 * senior gets redirected to `/` (the senior dashboard); admins / bots / null
 * also bounce to `/`. Mode is pinned by the route — the implementation file
 * (`agent-list-v1.tsx`) never branches on a role flag.
 *
 * SPEC-AdminImpersonation §"End-to-end chain coverage" item 2 race fix
 * (2026-05-29) — when `role === null` (auth query still resolving, or
 * cache just invalidated by `startImpersonation`), DO NOT call
 * `agentRouteRedirect`. The helper returns "/" for null, which fires a
 * Wouter <Redirect> that canonicalizes to "/qa/list" via QaList's own
 * setLocation. Net effect: impersonated Sarah at /qa/agent gets bounced
 * to /qa/list before RoleContext ever holds role:'agent'. The guard
 * below holds the page in a loading state until the auth payload lands,
 * then re-renders with the resolved role.
 */
import { lazy, Suspense } from "react";
import { Redirect } from "wouter";
import { Skeleton } from "@/components/ui/skeleton";
import { useRole } from "@/hooks/use-role";
import { agentRouteRedirect } from "@/lib/agent-ui-helpers";

const AgentListV1 = lazy(() => import("./agent-list-v1"));

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center h-full" data-testid="agent-list-loading">
      <Skeleton className="h-8 w-48" />
    </div>
  );
}

export default function AgentList() {
  const { role } = useRole();
  // SPEC-AdminImpersonation race fix — null-role guard.
  if (role === null) return <LoadingFallback />;
  const redirect = agentRouteRedirect(role, { kind: "list" });
  if (redirect) return <Redirect to={redirect} />;
  return (
    <Suspense fallback={<LoadingFallback />}>
      <AgentListV1 />
    </Suspense>
  );
}
